import { createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/api';
import { createWrapperPayload, wrapperConstants } from '../services/wrapper-api';
import { RootState } from '../store';

export const fetchWellTestData = createAsyncThunk(
  'rodliftwellstatus/fetchRodLiftData',
  async ({ wellId, installNumber }: { wellId: number; installNumber: number | null }) => {
    try {
      const requestPayload = createWrapperPayload(
        'GET',
        `/pumpchecker/api/v1/tests/${wellId}?${installNumber != null ? `installNumber=${installNumber}&` : ''}take=8&skip=0`,
      );

      return await api.post(wrapperConstants.router, requestPayload).then(function (response) {
        if (response) {
          return response.data;
        } else {
          return undefined;
        }
      });
    } catch (error) {
      return {};
    }
  },
);

export interface WellTestIdResponse {
  Name: string;
  WellId: number;
  MethodOfProduction: string;
  Identifier: string;
}

export const fetchWellTestId = createAsyncThunk<WellTestIdResponse | null, string>(
  'wellTestData/fetchWellTestId',
  async (assetId: string, { signal }) => {
    try {
      const controller = new AbortController();

      // Forward abort signal
      signal.addEventListener('abort', () => {
        controller.abort();
      });

      const requestPayload = createWrapperPayload('GET', `/pumpchecker/Well/GetWellId?externalIdentifier=${assetId}`);

      return await api
        .post(wrapperConstants.router, requestPayload, { signal: controller.signal })
        .then(function (response) {
          if (response) {
            return response.data;
          } else {
            return null;
          }
        });
    } catch (error) {
      console.error('Error fetching well test ID:', error);
      return null;
    }
  },
  {
    condition: (assetId, { getState }) => {
      const state = getState() as RootState;
      const { loading, lastFetchedAssetId } = state.WellTestIdSlice;

      // If assetId has changed, allow fetch even if loading
      if (lastFetchedAssetId && lastFetchedAssetId !== assetId) {
        return true;
      }

      // Don't fetch if already loading same asset
      if (loading) {
        return false;
      }

      // Only fetch if assetId has changed (redundant but safe)
      if (lastFetchedAssetId && lastFetchedAssetId === assetId) {
        return false;
      }

      return true;
    },
  },
);

export interface InstallationOption {
  InstallNumber: number;
  MethodOfProduction: string;
  StartDate: string;
  EndDate: string;
  Description: string;
}

export const fetchInstallationsForDropdown = createAsyncThunk<InstallationOption | null, string>(
  'wellTestData/fetchInstallationsForDropdown',
  async (wellId: string) => {
    try {
      const apiEndpoint = `/pumpchecker/api/v1/installation/installationsForDropDown/${wellId}`;
      const requestPayload = createWrapperPayload('GET', apiEndpoint);
      return await api.post(wrapperConstants.router, requestPayload).then(function (response) {
        if (response) {
          return response.data;
        } else {
          return null;
        }
      });
    } catch (error) {
      console.error('Error fetching installations for dropdown:', error);
      return null;
    }
  },
);
